import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Badge, Button, ButtonIcon, Col, DefaultTable, FormGroup, FormSection, InputGroup, Label, Row, SimpleAlert, TabsContent } from 'twyne-new.ui';
import { ChevronDown, Plus, Trash } from 'lucide-react';

import Input from 'components/form/Input';
import Switch from 'components/form/Switch';
import Select from 'components/form/Select';
import { DragHandle } from '@/components/SortableDnd';

import { useIntegrationStore } from '../../store';

import Delivery from './Delivery';
import { InputMergeTag, DropdownMergeTag } from './utils'
import { fileTypesOptions, delimiterOptions, quotecharOptions, indentOptions, tupleMergeTags, globalMergeTags } from './options'
import Propagable from 'components/Propagable';

const propTypes = Propagable.propTypes

const SortableRowContext = createContext(null)

function DndSortableTableRow({ children, row, className }) {
  const sortable = useSortable({
    id: String(row.index),
    attributes: {
      role: 'button',
      tabIndex: 0,
    },
  })
  const { setNodeRef, transform, transition, isDragging } = sortable

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 1 : 0,
        position: 'relative',
      }}
      className={className}
    >
      <SortableRowContext.Provider value={sortable}>
        {children}
      </SortableRowContext.Provider>
    </tr>
  )
}

function SweepFinishedGripColumn() {
  const sortable = useContext(SortableRowContext) || {}
  const { attributes, listeners } = sortable
  if (!attributes)
    return null

  return (
    <div className="flex items-center justify-center pt-2">
      <span className="cursor-grab" {...attributes} {...listeners}>
        <DragHandle />
      </span>
    </div>
  )
}

function SweepFinishedColumnsTable({
  columns,
  disabled,
  onSortEnd,
  handleChangeColumns,
  validateIsDuplicated,
  onRemoveRow,
}) {
  const sensors = useSensors(useSensor(MouseSensor, {}))
  const items = columns.map((_, i) => String(i))
  const tableData = columns.map((c, i) => ({ ...c, index: i }))

  const handleDragEnd = event => {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id)
      const newIndex = items.indexOf(over.id)
      if (oldIndex >= 0 && newIndex >= 0)
        onSortEnd({ oldIndex, newIndex })
    }
  }

  const tableColumns = [
    {
      dataField: '_grip',
      text: '',
      className: 'w-[3rem]',
      formatter: () => <SweepFinishedGripColumn />,
    },
    {
      dataField: 'colname',
      text: 'Field Name',
      formatter: (cell, row, index) => (
        <FormGroup>
          <Input
            required
            name={`configuration.columns[${index}].colname`}
            value={row.colname}
            onChange={evt => handleChangeColumns('colname', index)(evt.target.value)}
            validations={{
              isDuplicated: validateIsDuplicated(index),
            }}
            validationErrors={{
              isDuplicated: "Column name can't be duplicated",
            }}
          />
        </FormGroup>
      ),
    },
    {
      dataField: 'value',
      text: 'Field Value',
      formatter: (cell, row, index) => (
        <FormGroup>
          <InputMergeTag
            required
            mergetags={tupleMergeTags}
            id={`colval-${index}`}
            name={`configuration.columns[${index}].value`}
            value={row.value}
            onChange={handleChangeColumns('value', index)}
          />
        </FormGroup>
      ),
    },
    {
      dataField: '_mergetag',
      text: '',
      className: 'w-[6rem]',
      formatter: (cell, row, index) => (
        <DropdownMergeTag
          target={`colval-${index}`}
          onChange={handleChangeColumns('value', index)}
          mergetags={tupleMergeTags}
        >
          <div className='flex items-center gap-1 cursor-pointer'>
            <Badge size='lg' color='accent' variant='light'>
              MergeTag
            </Badge>
            <ChevronDown size={12} />
          </div>
        </DropdownMergeTag>
      ),
    },
    {
      dataField: '_actions',
      text: ' ',
      className: 'w-8',
      formatter: (_, __, index) => <ButtonIcon
        content="Delete"
        onClick={() => onRemoveRow(index)}
      >
        <Trash size={16} />
      </ButtonIcon>,
    },
  ]

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items}
        strategy={verticalListSortingStrategy}
      >
        <DefaultTable
          data={tableData}
          columns={tableColumns}
          bordered
          className="mt-2 max-h-[30vh] overflow-y-auto"
          noDataIndication={() => ' '}
          rowWrapper={DndSortableTableRow}
          sticky={false}
        />
      </SortableContext>
    </DndContext>
  )
}

export default class SweepContestFinishedForm extends Propagable {

  state = {
    file_name: '',
    file_type: 'csv',
    delimiter: 'comma',
    quotechar: 'doublequote',
    header: false,
    sweep_id: null,
    columns: [],
    sweepsOptions: [],
    scaIntegrations: [],
  }

  validateCredentialsSCA = (user, password) => {
    return !this.state.scaIntegrations.some(conf => {
      const { delivery: { sca } = {} } = conf || {}

      if (!sca)
        return false

      return sca.user == user && sca.password == password
    })
  }

  handleSortColumn = ({ oldIndex, newIndex = null }) => {
    const { columns } = this.state
    const deleted = columns.splice(oldIndex, 1)
    newIndex !== null && columns.splice(newIndex, 0, ...deleted)
    this.setState({ columns })
  }

  handleAddColumn = () => {
    const { columns } = this.state
    columns.push({ colname: '', value: '' })
    this.setState({ columns })
  }

  handleChangeColumns = (key, idx) => value => {
    const onChange = this.handleChange('columns')
    const { columns } = this.state
    columns[idx][key] = `${value}`.trim()
    onChange(columns)
  }

  validateIsDuplicated = idx => (_, value) => {
    const { columns } = this.state
    return !columns.find((col, colIdx) => col.colname === value && colIdx !== idx)
  }

  FileOptions = ({ disabled }) => {
    const { file_type } = this.state

    if (file_type === 'csv')
      return <this.CSVOptions disabled={disabled} />

    if (file_type === 'json')
      return <this.JSONOptions />

    return null
  }

  CSVOptions = ({ disabled }) => {
    const { delimiter, quotechar } = this.state

    return <>
      <Col size="6">
        <FormGroup>
          <Label>Delimiter Char</Label>
          <Select required disabled={disabled}
            name='configuration.delimiter'
            options={delimiterOptions}
            value={delimiter}
            clearable={false}
            setValue={this.handleChange('delimiter')}
          />
        </FormGroup>
      </Col>
      <Col size="6">
        <FormGroup>
          <Label>Quote Char</Label>
          <Select disabled={disabled}
            name='configuration.quotechar'
            options={quotecharOptions}
            value={quotechar}
            clearable={false}
            setValue={this.handleChange('quotechar')}
          />
        </FormGroup>
      </Col>
    </>
  }

  JSONOptions = () => {
    const { indent } = this.state

    return <Col sm="6">
      <FormGroup>
        <Label>Indent</Label>
        <Select
          name='configuration.indent'
          options={indentOptions}
          value={indent}
          clearable={false}
          setValue={this.handleChange('indent')}
        />
      </FormGroup>
    </Col>
  }

  TabDetails = ({ disabled, ...props }) => {
    const { file_name, file_type } = this.state

    return <TabsContent {...props}>
      <Row>
        <Col size="8">
          <FormGroup>
            <Label>File Name</Label>
            <InputGroup>
              <DropdownMergeTag target='file_name' onChange={this.handleChange('file_name')} mergetags={globalMergeTags}>
                <Button color='accent' className='flex items-center z-auto' disabled={disabled}>
                  MergeTag
                  <ChevronDown size={12} />
                </Button>
              </DropdownMergeTag>
              <InputMergeTag required disabled={disabled}
                mergetags={globalMergeTags}
                id='file_name'
                name='configuration.file_name'
                value={file_name}
                onChange={this.handleChange('file_name')}
              />
            </InputGroup>
          </FormGroup>
        </Col>
        <Col size="4">
          <FormGroup>
            <Label>File Type</Label>
            <Select required disabled={disabled}
              name='configuration.file_type'
              options={fileTypesOptions}
              value={file_type}
              clearable={false}
              setValue={this.handleChange('file_type')}
            />
          </FormGroup>
        </Col>
      </Row>
    </TabsContent>
  }

  TabConfigure = ({ disabled, ...props }) => {
    const { header, columns } = this.state

    return <TabsContent {...props}>
      <Row>
        <this.FileOptions disabled={disabled} />
        <Col size='12'>
          <div className="float-right">
            <Button color="accent" size='sm' onClick={this.handleAddColumn}>
              <Plus size={14} />
              Add column
            </Button>
          </div>
          <FormSection>
            <div className="flex gap-4">
              <div>
                Columns
              </div>
              <div className="flex items-center gap-2">
                <small>Include column headers</small>
                <Switch
                  name="configuration.header"
                  value={header || false}
                  setValue={this.handleChange('header')}
                />
              </div>
            </div>
          </FormSection>
          <SweepFinishedColumnsTable
            columns={columns}
            disabled={disabled}
            onSortEnd={this.handleSortColumn}
            handleChangeColumns={this.handleChangeColumns}
            validateIsDuplicated={this.validateIsDuplicated}
            onRemoveRow={idx => this.handleSortColumn({ oldIndex: idx })}
          />
        </Col>
      </Row>
    </TabsContent>
  }

  TabDelivery = ({ disabled, ...props }) => {
    return <TabsContent {...props}>
      <Delivery
        vendor={this.props.vendor}
        data={this.state.delivery}
        onChange={this.handleChange('delivery')}
        disabled={disabled}
        validateCredentialsSCA={this.validateCredentialsSCA.bind(this)} />
    </TabsContent>
  }

  VendorForm = ({ winners }) => {
    const { cid, gid } = this.state

    if (this.props.vendor === 'sca')
      return <React.Fragment>
        <Col size='3'>
          <FormGroup>
            <Label>Contract ID</Label>
            <Input required
              name='configuration.cid'
              value={cid}
              onChange={evt => this.handleChange('cid')(evt.target.value)}
            />
          </FormGroup>
        </Col>
        <Col size='3'>
          <FormGroup>
            <Label>GID</Label>
            <Input required
              type='number'
              name='configuration.gid'
              value={gid}
              onChange={evt => this.handleChange('gid')(evt.target.value)}
            />
          </FormGroup>
        </Col>
        <Col size='12'>
          <this.SCAWinnersAlert winners={winners} />
        </Col>
      </React.Fragment>

    return null
  }

  SCAWinnersAlert = ({ winners }) => {
    if (winners > 1)
      return <SimpleAlert variant='warning'>
        SCA Promotions only supports a single winner. The selected Sweeps has the "Number of Winners" configured for more than 1, this property be ignored for this integration and associated contests.
      </SimpleAlert>
    return null
  }

  SCAOptions = ({...props}) => {
    const [selectedSweepOption, setSelectedSweepOption] = useState(null)
    const [winners, setWinners] = useState(null)

    useEffect(() => {
      if (selectedSweepOption)
        setWinners(selectedSweepOption.winners)

    }, [selectedSweepOption])

    return <TabsContent {...props}>
      <Row>
        <Col size='6'>
          <FormGroup>
            <Label>Linked to Sweep</Label>
            <Select required
              name='configuration.sweep_id'
              options={this.state.sweepsOptions}
              value={this.state.sweep_id}
              clearable={false}
              handleOnChange={setSelectedSweepOption}
              setValue={this.handleChange('sweep_id')}
            />
          </FormGroup>
        </Col>
        <this.VendorForm winners={winners} />
      </Row>
    </TabsContent>
  }

  Content = ({ disabled }) => {
    const { collection: { item }, collections: { useLoad, items }, lists: { 'cms/posts': { items: posts, useLoad: useLoadPosts } } } = useIntegrationStore()
    useLoadPosts(useMemo(() => ({ content_type: 'sweep', loadSweep: true }), []))
    useLoad()

    const loadSweepsOptions = useCallback(async (integration, integrations, posts) => {
      const { id: currentIntegrationId } = integration || {}

      const scaIntegrations = integrations
        .filter(i => i.id != currentIntegrationId && i.vendor == 'sca')
        .map(i => i.configuration)

      const sweepsOptions = posts.map(({ id, title, sweep }) => ({
        label: `[#${id}] ${title}`,
        value: id,
        disabled: scaIntegrations.some(conf => conf.sweep_id == id),
        winners: sweep && sweep.winnerMaxCap
      }))

      this.setState({ scaIntegrations, sweepsOptions })
    }, [])

    useEffect(() => {
      loadSweepsOptions(item, items, posts)
    }, [item, items, loadSweepsOptions, posts])

    return <>
      <this.SCAOptions value='general_contest_finish' />
      <this.TabDetails value='details_contest_finish' disabled={disabled} />
      <this.TabConfigure value='configure' disabled={disabled} />
      <this.TabDelivery value='delivery' disabled={disabled} />
    </>
  }

  render() {
    if (this.props.integration_type !== 'contest_finish')
      return null

    const disabled = this.props.vendor === 'sca'

    return <this.Content disabled={disabled} />
  }
}

SweepContestFinishedForm.propTypes = propTypes
SweepContestFinishedForm.defaultProps = {}
